/**
 * Native camera + gallery helper.
 *
 * On Android (Capacitor) → opens the native Camera or Gallery picker
 * via @capacitor/camera, returns a JS `File` object so the rest of the
 * app can keep using the existing FormData upload pipeline.
 *
 * On Web → falls back to a hidden <input type="file" accept="image/*"
 * capture={"environment" | undefined}/> click.
 *
 * Every captured image is compressed (browser-image-compression on web,
 * native quality cap on Android) to ~0.8 MB before upload.
 */
import { isNativePlatform } from "./platform";
import imageCompression from "browser-image-compression";
// IMPORTANT: static import — dynamic imports of Capacitor plugins hang the
// bridge on Android 16 (SDK 36). Do not switch to `await import(...)`.
import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";

const NATIVE_QUALITY = 75;   // jpeg quality (Android)
const NATIVE_WIDTH = 1600;   // resize long edge
const WEB_MAX_MB = 0.8;
const WEB_MAX_WIDTH = 1600;

async function _dataUrlToFile(dataUrl, filename) {
  const r = await fetch(dataUrl);
  const blob = await r.blob();
  return new File([blob], filename, { type: blob.type || "image/jpeg" });
}

async function _compressWeb(file) {
  if (!file || !file.type?.startsWith("image/")) return file;
  if (file.size <= WEB_MAX_MB * 1024 * 1024 * 0.95) return file;
  try {
    const out = await imageCompression(file, {
      maxSizeMB: WEB_MAX_MB,
      maxWidthOrHeight: WEB_MAX_WIDTH,
      useWebWorker: true,
      initialQuality: 0.8,
    });
    // browser-image-compression returns a Blob; coerce back to File for consistent name
    return new File([out], file.name || "photo.jpg", { type: out.type || "image/jpeg" });
  } catch (e) {
    console.warn("image compression failed, sending original:", e?.message || e);
    return file;
  }
}

/**
 * Open the system camera (preferred on Android) and return a compressed File.
 * source: "CAMERA" | "GALLERY" | "PROMPT"
 */
export async function pickImage({ source = "PROMPT", filename = "photo.jpg" } = {}) {
  if (isNativePlatform()) {
    const srcMap = {
      CAMERA: CameraSource.Camera,
      GALLERY: CameraSource.Photos,
      PROMPT: CameraSource.Prompt,
    };
    const photo = await Camera.getPhoto({
      quality: NATIVE_QUALITY,
      allowEditing: false,
      resultType: CameraResultType.DataUrl,
      source: srcMap[source] || CameraSource.Prompt,
      saveToGallery: false,
      width: NATIVE_WIDTH,
      correctOrientation: true,
      promptLabelHeader: "Photo Add Karein",
      promptLabelPhoto: "Gallery se Chunein",
      promptLabelPicture: "Camera Se Lein",
      promptLabelCancel: "Cancel",
    });
    // Native side already compressed; just wrap as File.
    return _dataUrlToFile(photo.dataUrl, filename);
  }

  // Web fallback — synthetic <input type="file">
  return new Promise((resolve, reject) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    if (source === "CAMERA") input.capture = "environment";
    input.style.display = "none";
    document.body.appendChild(input);
    let resolved = false;
    input.onchange = async () => {
      resolved = true;
      const f = input.files?.[0];
      document.body.removeChild(input);
      if (!f) return resolve(null);
      try {
        const c = await _compressWeb(f);
        resolve(c);
      } catch (e) { reject(e); }
    };
    // Allow user to cancel without leaking the input element.
    setTimeout(() => {
      if (!resolved && input.parentNode) {
        // We can't reliably detect cancel; rely on focus + 60s GC
        const cleanup = () => { if (input.parentNode) document.body.removeChild(input); window.removeEventListener("focus", cleanup); };
        setTimeout(cleanup, 60_000);
        window.addEventListener("focus", cleanup);
      }
    }, 100);
    input.click();
  });
}

export const Source = { CAMERA: "CAMERA", GALLERY: "GALLERY", PROMPT: "PROMPT" };
